import { attach, createEffect, createEvent, forward, merge, sample } from 'effector';
import { combineEvents } from 'patronum/combine-events';

import { http } from 'model/helpers/http';
import { RootDomain } from 'model/root.model';
import { fetchCountryFx, ipAddress$ } from 'model/main.model';

interface RiskPayload {
	id: string,
	type: 'mobile' | 'desktop'
}

export const initMobileRisk = createEvent<{ sessionId: string }>();
export const initDesktopRisk = createEvent<{ sessionId: string }>();
export const destroyRisk = createEvent();

export const riskScript$ = RootDomain.store<HTMLElement | null>(null);
export const riskPayload$ = RootDomain.store<RiskPayload>(null);
export const riskHash$ = RootDomain.store<string>(null);

const mountRiskScriptFx = createEffect<void, HTMLElement>({
	handler: () => new Promise<HTMLElement>((res) => {
		const script = document.createElement('script');
		script.setAttribute('src', 'https://cdn.hive.id/risk-js/v4');
		script.onload = () => {
			res(script);
		};
		document.head.appendChild(script);
	}),
});

const configureRiskFx = createEffect<RiskPayload, string>({
	handler: async (payload: RiskPayload) => {
		// @ts-ignore
		const { risk } = window;

		const riskPayload: string = await new Promise((resolve, reject) => {
			risk.config({
				session_id: payload.id + '-' + payload.type,
				audio_fingerprint: true,
				canvas_fingerprint: true,
				webgl_fingerprint: true,
				onSuccess: function() {
					risk.getBase64Session(function(data) {
						if (data) {
							resolve(data);
						} else {
							reject();
						}
					});
				},
				onError: function() {
					reject();
				},
			});
		});

		return riskPayload;
	},
});
const uploadRiskFx = attach({
	effect: http.post,
	source: { risk: riskPayload$, ip: ipAddress$, hash: riskHash$ },
	mapParams: (_: void, { risk, ip, hash }) => ({
		url: `/sessions/${risk.id}/risk`,
		body: { analytics_data: hash, type: risk.type, ip },
	}),
});
const destroyRiskFx = createEffect({
	handler: (script: HTMLElement | null) => {
		document.removeChild(script);
	},
});

riskScript$
	.on(mountRiskScriptFx.doneData, (_, res) => res);
riskPayload$
	.on(initMobileRisk, (_, { sessionId }) => ({ id: sessionId, type: 'mobile' }))
	.on(initDesktopRisk, (_, { sessionId }) => ({ id: sessionId, type: 'desktop' }));
riskHash$
	.on(configureRiskFx.doneData, (_, payload) => payload);

forward({
	from: merge([
		initMobileRisk,
		initDesktopRisk,
	]),
	to: mountRiskScriptFx,
});
sample({
	source: riskPayload$,
	clock: mountRiskScriptFx.done,
	target: configureRiskFx,
});
forward({
	from: combineEvents({
		events: [
			configureRiskFx.finally,
			fetchCountryFx.finally,
		],
	}),
	to: uploadRiskFx,
});
sample({
	source: riskScript$,
	clock: destroyRisk,
	target: destroyRiskFx,
});
